import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { 
  ArrowLeft, Mail, Phone, Building2, Calendar, MapPin,
  MessageSquare, StickyNote, Clock, User, Reply, Check, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

export const InquiryDetails = () => {
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const { adminData, getAuthHeaders } = useAdminAuth();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInquiryDetails();
  }, [inquiryId]);

  const fetchInquiryDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inquiries/${inquiryId}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setInquiry(data);
      
      // Auto-mark as read
      if (data.status === 'new') {
        updateStatus('read');
      }
    } catch (error) {
      console.error('Error fetching inquiry:', error);
      toast.error('Failed to load inquiry');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await fetch(`${API_URL}/api/inquiries/${inquiryId}/status?status=${status}`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      setInquiry(prev => ({ ...prev, status }));
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/inquiries/${inquiryId}/notes?note_text=${encodeURIComponent(newNote)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      setInquiry(prev => ({
        ...prev,
        notes: [...(prev.notes || []), data.note]
      }));
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleReply = () => {
    setReplySubject(`Re: Inquiry from ${inquiry.full_name}`);
    setReplyContent(`
      <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <p>Dear ${inquiry.full_name},</p>
        <p>Thank you for your inquiry regarding <strong>${inquiry.service}</strong>.</p>
        <p>[Your message here]</p>
        <br>
        <p>Best regards,<br><strong>Unity Group Services</strong></p>
        <hr style="border: none; border-top: 1px solid #ddd; margin: 20px 0;">
        <p style="font-size: 12px; color: #666;">
          Unity Group Services - Your Complete Manpower Partner<br>
          Email: help.unityss@gmail.com | sales@unityssllp.com<br>
          Website: https://unityssllp.com
        </p>
      </div>
    `);
    setShowReplyModal(true);
  };

  const sendReply = async () => {
    if (!replySubject.trim() || !replyContent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      await fetch(`${API_URL}/api/inquiries/${inquiryId}/reply`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          inquiry_id: inquiryId,
          subject: replySubject,
          html_content: replyContent,
          admin_email: adminData.email
        })
      });

      toast.success('Email sent successfully!');
      setShowReplyModal(false);
      fetchInquiryDetails();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800 border-blue-200',
      read: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      replied: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[status] || styles.new;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading inquiry details...</p>
        </div>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-slate-600">Inquiry not found</p>
          <Button onClick={() => navigate('/admin/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Inquiry Details</h1>
                <p className="text-sm text-slate-600">ID: {inquiry.inquiry_id}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleReply}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Reply className="w-4 h-4 mr-2" />
                Reply via Email
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Customer Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-slate-600">Full Name</p>
                    <p className="font-medium text-slate-900">{inquiry.full_name}</p>
                  </div>
                </div>
                {inquiry.company_name && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-slate-600">Company</p>
                      <p className="font-medium text-slate-900">{inquiry.company_name}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-medium text-slate-900">{inquiry.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-slate-600">Phone</p>
                    <p className="font-medium text-slate-900">{inquiry.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-slate-600">Submitted</p>
                    <p className="font-medium text-slate-900">{formatDate(inquiry.submission_date)}</p>
                  </div>
                </div>
                {inquiry.ip_address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-slate-600">IP Address</p>
                      <p className="font-medium text-slate-900">{inquiry.ip_address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Message */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                Inquiry Message
              </h2>
              <div className="mb-3">
                <span className="text-sm font-medium text-slate-600">Service Required: </span>
                <span className="text-sm text-slate-900">{inquiry.service}</span>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-slate-900 whitespace-pre-wrap">{inquiry.message}</p>
              </div>
            </div>

            {/* Email History */}
            {inquiry.email_history && inquiry.email_history.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Email History</h2>
                <div className="space-y-4">
                  {inquiry.email_history.map((email) => (
                    <div key={email.email_id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-slate-900">{email.subject}</p>
                        <span className="text-xs text-slate-500">{formatDate(email.sent_at)}</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        To: {email.recipient} | From: {email.sent_by}
                      </p>
                      <div className="text-sm text-slate-600 bg-slate-50 rounded p-3 max-h-40 overflow-y-auto"
                           dangerouslySetInnerHTML={{ __html: email.content }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Status</h3>
              <div className={`inline-flex items-center px-3 py-2 rounded-lg border text-sm font-medium ${getStatusBadge(inquiry.status)}`}>
                {inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1)}
              </div>
              <div className="mt-4 space-y-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => updateStatus('read')}
                  disabled={inquiry.status === 'read'}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark as Read
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => updateStatus('closed')}
                  disabled={inquiry.status === 'closed'}
                >
                  <X className="w-4 h-4 mr-2" />
                  Mark as Closed
                </Button>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-blue-600" />
                Internal Notes
              </h3>
              
              {/* Add Note */}
              <div className="mb-4">
                <Textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="mb-2"
                  rows={3}
                />
                <Button size="sm" onClick={addNote} className="w-full">
                  Add Note
                </Button>
              </div>

              {/* Notes List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {inquiry.notes && inquiry.notes.length > 0 ? (
                  inquiry.notes.map((note) => (
                    <div key={note.note_id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-slate-900 mb-2">{note.note}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        {formatDate(note.created_at)} by {note.admin_name}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No notes yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reply Modal */}
      {showReplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Reply via Email</h2>
              <p className="text-sm text-slate-600 mt-1">To: {inquiry.email}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReplyModal(false)}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                onClick={sendReply}
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
